import * as Form from "@radix-ui/react-form"
import { PrimaryButton } from "components/Buttons/Button"
import { graphql, useStaticQuery } from "gatsby"
import BackgroundIMG from "images/contact/ContactBackground.svg"
import { ReactComponent as EOSSVG } from "images/global/badges/CloudCompliance_EasiestSetup_EaseOfSetup1.svg"
import { ReactComponent as EOUSVG } from "images/global/badges/CloudCompliance_EasiestToUse_Small-Business_EaseOfUse1.svg"
import { ReactComponent as NPSVG } from "images/global/badges/CloudCompliance_UsersMostLikelyToRecommend_Small-Business_Nps1.svg"
import { ReactComponent as HPSVG } from "images/global/badges/CloudEmailSecurity_HighPerformer_Small-Business_HighPerformer1.svg"
import { ReactComponent as BSSVG } from "images/global/badges/CloudSecurity_BestSupport_Small-Business_QualityOfSupport1.svg"
import { ReactComponent as EASVG } from "images/global/badges/EmailSecurity_EasiestAdmin_EaseOfAdmin1.svg"
import { ReactComponent as EDSVG } from "images/global/badges/EmailSecurity_EasiestToDoBusinessWith_EaseOfDoingBusinessWith1.svg"
import Cookies from "js-cookie"
import AutoAnimate from "library/AutoAnimate"
import UniversalLink from "library/Loader/UniversalLink"
import UniversalImage from "library/UniversalImage"
import { fmobile, fresponsive, ftablet } from "library/fullyResponsive"
import useMedia from "library/useMedia"
import { useEffect, useRef, useState } from "react"
import { InlineWidget } from "react-calendly"
import styled, { css } from "styled-components"
import colors from "styles/colors"
import textStyles from "styles/text"
import links from "utils/links"
import Checkbox from "./Checkbox"
import FormDropdown from "./FormDropdown"
import TextInput from "./TextInput"

const portalId = "143321007"

const getCookie = (name: string) => {
	return Cookies.get(name) ?? null
}

export default function ContactPage({
	title,
	subtitle,
	kicker,
	formId,
	formName,
	customSuccessMessage,
}: {
	title: string
	subtitle: string
	kicker: string
	formId: string
	formName: string
	customSuccessMessage?: string
}) {
	const endpoint = `https://api.hsforms.com/submissions/v3/integration/submit/${portalId}/${formId}`
	const [formState, setFormState] = useState<
		"idle" | "submitting" | "calendly"
	>("idle")
	const [meetingScheduled, setMeetingScheduled] = useState(false)
	const [error, setError] = useState(false)
	const [errorMessage, setErrorMessage] = useState<string>(
		"Something went wrong! Check your internet connection and try again in a moment.",
	)
	const [formData, setFormData] = useState<{
		firstName: string
		lastName: string
		email: string
	}>({
		firstName: "",
		lastName: "",
		email: "",
	})
	const responsive = useMedia(false, false, true, true)
	const showCalendly = formState === "calendly"
	const calendlyContainerRef = useRef<HTMLDivElement>(null)

	const icon: Queries.ContactIconQuery = useStaticQuery(graphql`
		query ContactIcon {
			contactIcon: file(relativePath: { eq: "contact/contactIcon.png" }) {
				childImageSharp {
					gatsbyImageData(placeholder: NONE)
				}
			}
		}
	`)

	// Load Calendly script
	useEffect(() => {
		// Only load if we're in the browser
		if (typeof window === "undefined") return

		// Check if script is already loaded
		if (
			document.querySelector(
				'script[src="https://assets.calendly.com/assets/external/widget.js"]',
			)
		) {
			return
		}

		// Load the script
		const script = document.createElement("script")
		script.src = "https://assets.calendly.com/assets/external/widget.js"
		script.async = true
		document.body.appendChild(script)
	}, [])

	// Set up Calendly event listener
	useEffect(() => {
		if (!showCalendly || typeof window === "undefined") return

		// Add Calendly event listener
		const handleCalendlyEvent = (e: MessageEvent) => {
			if (e.data.event && e.data.event.indexOf("calendly") === 0) {
				if (e.data.event === "calendly.event_scheduled") {
					setMeetingScheduled(true)
				}
			}
		}

		window.addEventListener("message", handleCalendlyEvent)

		// Clean up
		return () => {
			window.removeEventListener("message", handleCalendlyEvent)
		}
	}, [showCalendly])

	return (
		<Wrapper>
			<Background src={BackgroundIMG} alt="" />
			<Copy>
				<Kicker>
					<KickerIcon image={icon.contactIcon} alt="rotate logo" />
					<KickerText>{meetingScheduled ? "Success!" : kicker}</KickerText>
				</Kicker>
				<Title>{meetingScheduled ? "Success!" : title}</Title>
				<Subtitle>
					{meetingScheduled
						? (customSuccessMessage ??
							"Thank you for scheduling a demo. We look forward to meeting with you!")
						: subtitle}
				</Subtitle>
				{!responsive && (
					<Badges>
						<EOSSVG />
						<EOUSVG />
						<NPSVG />
						<HPSVG />
						<BSSVG />
						<EASVG />
						<EDSVG />
					</Badges>
				)}
			</Copy>

			{formState === "calendly" ? (
				<FormContainer ref={calendlyContainerRef}>
					<InlineWidget
						url="https://calendly.com/d/cnst-t66-3yv/rotate-demo"
						styles={{
							height: "500px",
							width: "100%",
						}}
						pageSettings={{
							backgroundColor: "1a1a1a",
							textColor: "ffffff",
							primaryColor: "ff8d3a",
							hideGdprBanner: true,
						}}
						prefill={{
							email: formData.email,
							firstName: formData.firstName,
							lastName: formData.lastName,
							name: `${formData.firstName} ${formData.lastName}`,
						}}
					/>
				</FormContainer>
			) : (
				<FormWrapper
					name={formName}
					// @ts-expect-error update me when react 19 is out! this will stop working then
					inert={formState !== "idle" ? "" : undefined}
					style={{
						opacity: formState === "submitting" ? 0.5 : 1,
					}}
					onSubmit={(e) => {
						e.preventDefault()
						setFormState("submitting")
						setError(false)
						const formData = new FormData(e.currentTarget)

						// Save form data for Calendly prefill
						setFormData({
							firstName: (formData.get("firstName") as string) || "",
							lastName: (formData.get("lastName") as string) || "",
							email: (formData.get("email") as string) || "",
						})

						fetch(endpoint, {
							method: "POST",
							headers: {
								"Content-Type": "application/json",
							},
							body: JSON.stringify({
								context: {
									hutk: getCookie("hubspotutk"),
								},
								fields: [
									{
										name: "firstname",
										value: formData.get("firstName") ?? "",
									},
									{
										name: "lastname",
										value: formData.get("lastName") ?? "",
									},
									{
										name: "email",
										value: formData.get("email") ?? "",
									},
									{
										name: "company",
										value: formData.get("companyName") ?? "",
									},
									{
										name: "mobilephone",
										value: formData.get("phone") ?? "",
									},
									{
										name: "job_role",
										value: formData.get("jobRole") ?? "",
									},
									{
										name: "message",
										value: formData.get("message") ?? "",
									},
								],
							}),
						})
							.then(async (res) => {
								if (res.ok) {
									window.scrollTo({
										top: 0,
										behavior: "smooth",
									})
									setFormState("calendly")
								} else {
									setFormState("idle")
									setError(true)

									try {
										// Parse the error response
										const errorData = await res.json()
										console.error("Form submission error:", errorData)

										// Check for BLOCKED_EMAIL error type
										if (
											errorData &&
											typeof errorData === "object" &&
											"errors" in errorData &&
											Array.isArray(errorData.errors) &&
											errorData.errors.length > 0
										) {
											// Define error interface
											interface FormError {
												errorType: string
												[key: string]: unknown
											}

											// Find BLOCKED_EMAIL error
											const blockedEmailError = errorData.errors.find(
												(err): err is FormError =>
													err !== null &&
													typeof err === "object" &&
													"errorType" in err &&
													typeof (err as FormError).errorType === "string" &&
													(err as FormError).errorType === "BLOCKED_EMAIL",
											)

											if (blockedEmailError) {
												setErrorMessage(
													"Please use your business email address",
												)
											} else {
												setErrorMessage(
													"Something went wrong! Check your internet connection and try again in a moment.",
												)
											}
										}
									} catch (parseError) {
										console.error("Error parsing response:", parseError)
										setErrorMessage(
											"Something went wrong! Check your internet connection and try again in a moment.",
										)
									}
								}
							})
							.catch((error) => {
								console.error(error)
								setFormState("idle")
								setError(true)
								setErrorMessage(
									"Something went wrong! Check your internet connection and try again in a moment.",
								)
							})
					}}
				>
					<Row>
						<TextInput name="firstName" placeholder="First Name" required />
						<TextInput name="lastName" placeholder="Last Name" required />
					</Row>
					<TextInput name="companyName" placeholder="Company Name" required />
					<TextInput
						name="email"
						type="email"
						placeholder="Business Email"
						required
					/>
					<TextInput
						name="phone"
						type="tel"
						placeholder="Phone Number"
						required
					/>
					<FormDropdown
						required
						name="jobRole"
						placeholder="I am a…"
						options={[
							"Company",
							"Managed Service Provider",
							"Insurance Partner",
							"Technology Partner",
							"Other",
						]}
					/>
					<TextInput
						name="comments"
						type="textarea"
						placeholder="Additional comments, questions or requests"
					/>
					{useMedia(null, null, <div />, <div />)}
					<Checkbox name="privacy" required>
						I agree to receive emails and accept
						<br /> the terms of the{" "}
						<UniversalLink to={links.privacyPolicy}>
							privacy policy.
						</UniversalLink>
					</Checkbox>
					{error ? <SubmissionError>{errorMessage}</SubmissionError> : <div />}
					<Form.Submit asChild>
						<Submit color="teal" type="submit">
							Book a demo
						</Submit>
					</Form.Submit>
				</FormWrapper>
			)}
		</Wrapper>
	)
}

const Background = styled.img`
	${fresponsive(css`
		position: absolute;
		z-index: -1;
		top: 142px;
		left: 695px;
		width: 1242px;
		height: 813px;
		max-width: unset;
		max-height: unset;
		mask-image: linear-gradient(
			to right,
			white 0%,
			white calc(100% - 200px),
			transparent 100%
		);
		object-fit: fill;
	`)}

	${ftablet(css`
		top: 575px;
		left: 158px;
		height: 840px;
	`)}

	${fmobile(css`
		display: none;
	`)}
`

const Wrapper = styled.div`
	${fresponsive(css`
		position: relative;
		padding: 210px 138px 146px;
		display: grid;
		grid-template-columns: 501.8px 411px;
		gap: 168px;
		place-items: start;
		max-width: 1440px;
		margin: 0 auto;
	`)}

	${ftablet(css`
		grid-template-columns: 1fr;
		padding: 180px 269px 113px;
		gap: 159px;
	`)}
	${fmobile(css`
		grid-template-columns: 1fr;
		padding: 130px 23px 101px;
		gap: 35px;
	`)}
`

const Copy = styled.div`
	display: flex;
	flex-direction: column;
	justify-content: center;

	${fresponsive(css`
		min-height: 380px;
	`)}

	${ftablet(css`
		min-height: unset;
		place-items: center;
		text-align: center;
		width: 100%;
	`)}
	${fmobile(css`
		min-height: unset;
		place-items: center;
		text-align: center;
		width: 100%;
	`)}
`

const Title = styled.h1`
	${textStyles.h3};
	color: white;

	${fresponsive(css`
		width: 400px;
		margin-top: 18px;
	`)}

	${ftablet(css`
		${textStyles.h2};
		margin-top: 24px;
		width: unset;
	`)}
	${fmobile(css`
		${textStyles.h6};
		margin-top: 12px;
		text-wrap: balance;
		width: 230px;
	`)}
`

const Subtitle = styled.h2`
	${textStyles.body1R};
	color: ${colors.gray600};
	${fresponsive(css`
		margin-top: 24px;
		max-width: 323px;
	`)}

	${ftablet(css`
		${textStyles.body1L};
		margin-top: 36px;
	`)}

	${fmobile(css`
		${textStyles.body1S};
		width: 250px;
	`)}
`

const FormWrapper = styled(Form.Root)`
	${fresponsive(css`
		display: grid;
		gap: 12px;
		width: 100%;
		transition: opacity 0.5s ease;
	`)}
`

const Row = styled.div`
	display: grid;
	${fresponsive(css`
		gap: 12px;
		grid-template-columns: 203px 1fr;
	`)}
	${ftablet(css`
		grid-template-columns: 237px 1fr;
	`)}

	${fmobile(css`
		grid-template-columns: 1fr;
	`)}
`

const Submit = styled(PrimaryButton)`
	${fresponsive(css`
		width: 140px;
		margin: 0 auto;
	`)}

	${ftablet(css`
		width: auto;
	`)}
	${fmobile(css`
		width: auto;
	`)}
`

const Kicker = styled.div`
	display: flex;
	align-items: center;
	${fresponsive(css`
		gap: 12px;
	`)}
`
const KickerIcon = styled(UniversalImage)`
	${fresponsive(css`
		width: 48px;
		height: 48px;
		flex-shrink: 0;
	`)}
`
const KickerText = styled.div`
	${textStyles.kicker3};
	color: ${colors.gray700};
`

const PaddedAnimate = styled(AutoAnimate)`
	${fresponsive(css`
		width: 100% !important;
		margin: -100vh -10px;
		padding: 100vh 10px;
	`)}
`

const SubmissionError = styled.div`
	${textStyles.body2L};
	color: ${colors.productRed1};
	text-align: center;
`

const Badges = styled.div`
	position: relative;

	${fresponsive(css`
		width: 470px;
		height: 64px;
		display: flex;
		gap: 20.94px;
		margin: 28px 0 0;

		svg {
			width: 49.2px;
			height: 63.85px;
		}
	`)}

	${ftablet(css`
		position: absolute;
		flex-direction: column;
		right: 80px;
		top: 660px;
		width: 50px;
		height: 600px;
		z-index: 3;
		margin: unset;
	`)}

	${fmobile(css`
		flex-shrink: 0;
		width: 327px;
		height: auto;
		flex-wrap: wrap;
		margin: 36px 0 36px 18px;
	`)}
`

const FormContainer = styled.div`
	position: relative;
	z-index: 10;
	
	${fresponsive(css`
		display: grid;
		width: 100%;
		transition: opacity 0.5s ease;
	`)}

	${ftablet(css`
		width: 100%;
	`)}

	${fmobile(css`
		width: 327px;
	`)}
`
